import React from "react";

interface SocialButtonProps {
  href: string;
  ariaLabel: string;
  children: React.ReactNode;
}

export function SocialButton({ href, ariaLabel, children }: SocialButtonProps) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      className="w-10 h-10 rounded-full bg-[#e7f1f1] flex items-center justify-center hover:scale-110 transition-transform focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#01E12D]"
    >
      {children}
    </a>
  );
}

// Social Media Icons as separate components for reusability
export function TwitterIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 16 16" aria-hidden="true">
      <path 
        d="M14.6667 2.66667C14.6667 2.66667 14.2 4.06667 13.3333 4.93333C14.4 11.6 7.06667 16.4667 1.33333 12.6667C2.8 12.7333 4.26667 12.2667 5.33333 11.3333C2 10.3333 0.333333 6.4 2 3.33333C3.46667 5.06667 5.73333 6.06667 8 6C7.4 3.2 10.6667 1.6 12.6667 3.46667C13.4 3.46667 14.6667 2.66667 14.6667 2.66667Z" 
        fill="currentColor" 
      />
    </svg>
  );
}

export function InstagramIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 16 16" aria-hidden="true">
      <path 
        d="M11.3333 1.33333H4.66667C2.82572 1.33333 1.33333 2.82572 1.33333 4.66667V11.3333C1.33333 13.1743 2.82572 14.6667 4.66667 14.6667H11.3333C13.1743 14.6667 14.6667 13.1743 14.6667 11.3333V4.66667C14.6667 2.82572 13.1743 1.33333 11.3333 1.33333Z" 
        stroke="currentColor" 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth="2" 
      />
      <path 
        d="M10.6667 7.58C10.7489 8.13483 10.6542 8.70148 10.3958 9.19934C10.1375 9.69721 9.72876 10.1009 9.22775 10.3531C8.72674 10.6053 8.15897 10.6931 7.60519 10.604C7.05142 10.5148 6.53984 10.2534 6.14323 9.85677C5.74661 9.46016 5.48515 8.94858 5.39605 8.39481C5.30694 7.84103 5.39471 7.27326 5.64688 6.77225C5.89906 6.27124 6.30279 5.86249 6.80066 5.60416C7.29852 5.34583 7.86517 5.25106 8.42 5.33333C8.98595 5.41726 9.5099 5.68098 9.91446 6.08554C10.319 6.4901 10.5827 7.01405 10.6667 7.58Z" 
        stroke="currentColor" 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth="2" 
      />
      <circle cx="11.5" cy="4.5" r="0.75" fill="currentColor" />
    </svg>
  );
}

export function LinkedInIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 55 55" aria-hidden="true">
      <path 
        d="M43.2 0H11.37C5.09 0 0 5.09 0 11.37V43.2C0 49.48 5.09 54.57 11.37 54.57H43.2C49.48 54.57 54.57 49.48 54.57 43.2V11.37C54.57 5.09 49.48 0 43.2 0ZM18.19 43.2H11.37V18.19H18.19V43.2ZM14.78 15.31C12.58 15.31 10.8 13.51 10.8 11.3C10.8 9.08 12.58 7.29 14.78 7.29C16.98 7.29 18.76 9.08 18.76 11.3C18.76 13.51 16.98 15.31 14.78 15.31ZM45.48 43.2H38.65V30.46C38.65 22.8 29.56 23.38 29.56 30.46V43.2H22.74V18.19H29.56V22.2C32.73 16.32 45.48 15.89 45.48 27.83V43.2Z" 
        fill="currentColor" 
      />
    </svg>
  );
}

export function GitHubIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 16 16" aria-hidden="true">
      <path 
        d="M10 14.6667V12C10.0927 11.1649 9.85329 10.3267 9.33333 9.66667C11.3333 9.66667 13.3333 8.33333 13.3333 6C13.3867 5.16667 13.1533 4.34667 12.6667 3.66667C12.8533 2.9 12.8533 2.1 12.6667 1.33333C12.6667 1.33333 12 1.33333 10.6667 2.33333C8.90667 2 7.09333 2 5.33333 2.33333C4 1.33333 3.33333 1.33333 3.33333 1.33333C3.13333 2.1 3.13333 2.9 3.33333 3.66667C2.84792 4.34392 2.61231 5.16852 2.66667 6C2.66667 8.33333 4.66667 9.66667 6.66667 9.66667C6.40667 9.99333 6.21333 10.3667 6.1 10.7667C5.98667 11.1667 5.95333 11.5867 6 12V14.6667" 
        stroke="currentColor" 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth="1.5" 
      />
      <path 
        d="M6 12C2.99333 13.3333 2.66667 10.6667 1.33333 10.6667" 
        stroke="currentColor" 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth="1.5" 
      />
    </svg>
  );
}
